import { useTracking } from 'react-tracking';
import { format } from 'date-fns';
import { convert } from 'html-to-text';
import { truncate, kebabCase } from 'lodash';

import { Grid, Heading, Text } from '@simplywallst/ui-core';
import { toCurrency } from '@/utilities/formatting';

import { useCloudflareImage } from '@/hooks/useCloudflareImage';
import type { NarrativeFragmentType } from '@/pages/NarrativeList/hooks/narrative-fragment';

import {
  AuthorImage,
  StyledButton,
  GridWrapper,
  HeadingLinkWrapper,
  NarrativeWrapper,
  ValuationHistogramLinkWrapper,
  ValuationHistogramWrapper,
  ViewNarrativeLink,
} from './styled';

import loadable from '@loadable/component';

const ValuationHistogram = loadable(
  () =>
    import('@/pages/Narrative/components/AuthorValuation/ValuationHistogram'),
  {
    resolveComponent: (component) => component.ValuationHistogram,
  }
);

const NarrativeVoting = loadable(
  () => import('@/pages/Narrative/components/NarrativeVoting/NarrativeVoting')
);

const CompanyNarrative = ({
  narrative,
}: {
  narrative: NarrativeFragmentType;
}) => {
  const {
    author,
    company,
    contentBlocks,
    currencyIso,
    id,
    slug,
    title,
    valuations,
    updatedAt,
    voteCount,
  } = narrative;

  const authorImageUrl = useCloudflareImage({
    src: author.imageUrl,
    width: 60,
    height: 60,
    fit: 'cover',
  });

  const { Track: NarrativeTrack, trackEvent: narrativeTrackEvent } =
    useTracking({
      type: 'track',
      subject: 'narrative',
      narrativeId: id,
      companyId: company.id,
      uniqueSymbol: company.tickerSymbol,
    });

  const baseCase = valuations?.find((v) => v.type === 'base');

  if (typeof baseCase === 'undefined') {
    return null;
  }

  const keyTakeaways = contentBlocks.find((c) => c.title === 'Key Takeaways');

  if (typeof keyTakeaways === 'undefined') {
    return null;
  }

  const keyTakeawaysText = convert(keyTakeaways?.contentHtml || '', {
    wordwrap: false,
    // @ts-expect-error - FIXME
    ignoreHref: true,
    selectors: [
      {
        selector: 'ul',
        format: 'unorderedList',
        options: {
          itemPrefix: ' ',
        },
      },
      {
        selector: 'a',
        format: 'anchor',
        options: {
          ignoreHref: true,
        },
      },
    ],
  });

  const truncatedKeyTakeawaysText = truncate(keyTakeawaysText, { length: 120 });

  return (
    <NarrativeTrack>
      <NarrativeWrapper as="article" data-cy-id={`company-narrative-${slug}`}>
        <GridWrapper
          gap="8px"
          templateColumns="32px max-content"
          alignItems="center"
        >
          <AuthorImage src={authorImageUrl} alt={kebabCase(author.name)} />
          <Grid>
            <StyledButton
              as="a"
              href={author.url}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                narrativeTrackEvent({ modifier: 'author', action: 'click' })
              }
            >
              <Text
                typography="small"
                color="white"
                data-cy-id="company-narrative-author-name"
              >
                {author.name}
              </Text>
            </StyledButton>
            <Text
              typography="tiny"
              color="mediumGray"
              data-cy-id="company-narrative-author-role"
            >
              {author.role}
            </Text>
          </Grid>
        </GridWrapper>
        <GridWrapper gap="16px">
          <HeadingLinkWrapper
            to={`/narratives/${slug}`}
            onClick={() => {
              narrativeTrackEvent({
                action: 'click',
                modifier: 'view',
              });
            }}
          >
            <Heading
              as="h2"
              typography="smallStrong"
              data-cy-id="company-narrative-heading"
            >
              {title}
            </Heading>
            <Text
              typography="xSmall"
              color="lightGray"
              data-cy-id="company-narrative-content"
            >
              {truncatedKeyTakeawaysText}
            </Text>
          </HeadingLinkWrapper>
        </GridWrapper>
        <ValuationHistogramLinkWrapper
          to={`/narratives/${slug}`}
          onClick={() => {
            narrativeTrackEvent({
              action: 'click',
              modifier: 'view',
            });
          }}
        >
          <ValuationHistogramWrapper gap="16px">
            <ValuationHistogram
              sharePrice={company.analysisValue.lastSharePrice as number}
              authorFairValue={baseCase.fairValue}
              authorImageUrl={author.imageUrl}
              currencyISO={currencyIso}
            />
            <Text
              typography="tiny"
              color="mediumGray"
              textAlign="center"
              data-cy-id="company-narrative-price-at-publication"
            >
              Price at publication{' '}
              {toCurrency(baseCase.sharePriceAtPublish, 2, currencyIso)}
            </Text>
          </ValuationHistogramWrapper>
        </ValuationHistogramLinkWrapper>
        <GridWrapper gap="16px">
          <ViewNarrativeLink
            styleType="loudLarge"
            to={`/narratives/${slug}`}
            data-cy-id="company-narrative-view-narrative"
            onClick={() => {
              narrativeTrackEvent({
                action: 'click',
                modifier: 'view',
              });
            }}
          >
            View narrative
          </ViewNarrativeLink>
          <Grid
            gap="8px"
            autoFlow="column"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text
              typography="tiny"
              color="mediumGray"
              textAlign="left"
              data-cy-id="company-narrative-last-updated"
            >
              Updated{' '}
              <time dateTime={updatedAt}>
                {format(new Date(updatedAt), 'dd MMM yyyy')}
              </time>
            </Text>
            <NarrativeVoting
              narrativeId={id}
              slug={slug}
              upVotes={voteCount.upvotes}
              companyId={company.canonicalURL}
            />
          </Grid>
        </GridWrapper>
      </NarrativeWrapper>
    </NarrativeTrack>
  );
};

export default CompanyNarrative;
